<script lang="ts">
    import { fade } from "svelte/transition";
    import { onMount } from "svelte";
    import type { ProximityNotification } from "../../Stores/ProximityNotificationStore";
    import { chatNotificationStore } from "../../Stores/ProximityNotificationStore";
    import { chatVisibilityStore } from "../../Stores/ChatStore";
    import { selectedRoomStore } from "../../Chat/Stores/SelectRoomStore";
    import { navChat } from "../../Chat/Stores/ChatStore";
    import LL from "../../../i18n/i18n-svelte";
    import MessageNotificationIcon from "../Icons/meta/MessageNotificationIcon.svelte";
    import { focusNotificationTarget } from "./NotificationRoomFocus";

    interface Props {
        notification: ProximityNotification;
    }

    let { notification }: Props = $props();

    let roomType = $derived(notification.room.type);
    let roomName = $derived(notification.room.name);

    const NOTIFICATION_DURATION = 10000; // 10 seconds

    onMount(() => {
        const timeout = setTimeout(() => {
            chatNotificationStore.removeNotification(notification.id);
        }, NOTIFICATION_DURATION);

        return () => {
            clearTimeout(timeout);
        };
    });

    function handleClick() {
        chatVisibilityStore.set(true);
        navChat.switchToChat();

        if (notification.openRoomOnClick !== false) {
            const room = notification.room;
            chatNotificationStore.clearRoom(room.id);
            room.setTimelineAsRead();
            selectedRoomStore.set(room);
            focusNotificationTarget(room, notification.messageId, notification.sidePanelSection);
        } else {
            chatNotificationStore.clearAll();
            // Open the chat on the main chat panel
            selectedRoomStore.set(undefined);
        }
    }
</script>

<div
    class="proximity-notification bg-mlight/80 shadow-light rounded-md2 backdrop-blur-md flex gap-3 py-3 pl-5 pr-2 pointer-events-auto z-900 cursor-pointer hover:bg-mlight-darker/90 transition-colors text-mdark w-[60%] min-w-[300px] max-w-[600px]"
    onclick={handleClick}
    role="button"
    tabindex="0"
    aria-live="polite"
    aria-atomic="true"
    onkeydown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleClick();
        }
    }}
    out:fade={{ duration: 150 }}
>
    <div class="mt-1 text-mdark text-xl shrink-0"><MessageNotificationIcon /></div>

    <div class="flex flex-col text-mdark flex-1 min-w-0 overflow-hidden">
        <div class="font-semibold mb-2 text-base text-mdark truncate tracking-normal align-middle">
            {notification.userName}
            {#if $roomType !== "direct"}
                {$LL.chat.notification.in()}
                {$roomName}
            {/if}
        </div>
        <div
            data-testid="proximity-notification-message"
            class="font-normal text-base tracking-normal text-mdark line-clamp-2"
        >
            {notification.message}
        </div>
    </div>
</div>

<style>
    @keyframes slide-up {
        from {
            transform: translateY(100%);
            opacity: 0;
        }
        to {
            transform: translateY(0);
            opacity: 1;
        }
    }

    .proximity-notification {
        animation: slide-up 0.3s ease-out;
    }
</style>
